import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalShipping as ShipIcon,
} from "@mui/icons-material";
import { useTSQShipmentList } from "../../hooks/useTSShipments";
import { ListRequestFilter } from "../../types/common/commonTypes";
import { ShipmentRecordFull, ShipmentStatus } from "../../types/shipmentsTypes";
import { paginationDefaults } from "../../config/settingsDefaults";
import { useNavigate } from "react-router-dom";
import stringUtils from "../../utils/stringUtils";

export const PageShipmentList: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });

  const [orderBy, setOrderBy] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const {
    data: shipResponse,
    isLoading: shipsLoading,
    error: queryError,
    isPlaceholderData,
  } = useTSQShipmentList({
    active_status: statusFilter,
    filters: [[]],
  });
  const [shipments, totalCount] = useMemo(() => {
    let shipments: ShipmentRecordFull[] = [];
    let count: number = 0;
    if (shipResponse?.items) {
      shipments = shipResponse.items;
      count = shipments.length;
    }
    return [shipments, count];
  }, [shipResponse]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getSortValue = (shipObj: ShipmentRecordFull, property: string): any => {
    switch (property) {
      case "name":
        return shipObj.name.toLowerCase();
      case "status":
        return shipObj.status.toLowerCase();
      case "reason":
        if (shipObj.reason === "OTHER") {
          return `OTHER: ${shipObj.reason_other}`.toLowerCase();
        }
        return shipObj.reason.toLowerCase();
      case "carrier":
        if (shipObj.carrier === "OTHER") {
          return `OTHER: ${shipObj.carrier_other}`.toLowerCase();
        }
        return shipObj.carrier.toLowerCase();
      case "method":
        return shipObj.method.toLowerCase();
      case "ship_to":
        return shipObj.ship_to.name.toLowerCase();
      case "ship_from":
        return shipObj.ship_from.name.toLowerCase();
      case "ship_date":
        return new Date(shipObj.shipment_date || "");
      case "created_at":
        return new Date(shipObj.created_at);
      default:
        return "";
    }
  };

  const filteredShipments = useMemo(() => {
    return shipments.filter((shipment) => {
      const lowerTerm = searchTerm.toLowerCase();
      const matchesSearch =
        lowerTerm === "" ||
        shipment.name.toLowerCase().includes(lowerTerm) ||
        shipment.ship_to.name.toLowerCase().includes(lowerTerm) ||
        shipment.ship_from.name.toLowerCase().includes(lowerTerm) ||
        shipment.reason.toLowerCase().includes(lowerTerm) ||
        (shipment.reason_other || "").toLowerCase().includes(lowerTerm) ||
        shipment.carrier.toLowerCase().includes(lowerTerm) ||
        (shipment.carrier_other || "").toLowerCase().includes(lowerTerm) ||
        shipment.method.toLowerCase().includes(lowerTerm);
      return matchesSearch;
    });
  }, [shipments, searchTerm]);

  const sortedShipments = useMemo(() => {
    return filteredShipments.sort((a, b) => {
      const aVal = getSortValue(a, orderBy);
      const bVal = getSortValue(b, orderBy);

      if (aVal === bVal) return 0;

      if (sortOrder === "asc") {
        return aVal < bVal ? -1 : 1;
      } else {
        return aVal > bVal ? -1 : 1;
      }
    });
  }, [filteredShipments, orderBy, sortOrder]);

  const getStatusColor = (
    status: ShipmentStatus,
  ):
    | "error"
    | "success"
    | "info"
    | "warning"
    | "primary"
    | "secondary"
    | "default" => {
    switch (status) {
      case "STAGING":
        return "default";
      case "HOLDING":
        return "warning";
      case "LABEL_CREATED":
        return "secondary";
      case "IN_TRANSIT":
        return "info";
      case "DELIVERED":
        return "primary";
      case "ERROR":
        return "error";
      case "VOID":
        return "warning";
      default:
        return "default";
    }
  };

  if (shipsLoading && shipments.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Shipments
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/shipments/form?mode=create")}
        >
          Create New Shipment
        </Button>
      </Stack>

      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An unknown error occurred"}
        </Alert>
      )}

      {isPlaceholderData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search Shipments by name, Ship From/To Addressee, reason, carrier, or method..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === "name" ? sortOrder : false}>
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? sortOrder : "asc"}
                  onClick={() => handleSort("name")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "status" ? sortOrder : false}
              >
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? sortOrder : "asc"}
                  onClick={() => handleSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "reason" ? sortOrder : false}
              >
                <TableSortLabel
                  active={orderBy === "reason"}
                  direction={orderBy === "reason" ? sortOrder : "asc"}
                  onClick={() => handleSort("reason")}
                >
                  Reason
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "carrier" ? sortOrder : false}
              >
                <TableSortLabel
                  active={orderBy === "carrier"}
                  direction={orderBy === "carrier" ? sortOrder : "asc"}
                  onClick={() => handleSort("carrier")}
                >
                  Carrier
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "method" ? sortOrder : false}
              >
                <TableSortLabel
                  active={orderBy === "method"}
                  direction={orderBy === "method" ? sortOrder : "asc"}
                  onClick={() => handleSort("method")}
                >
                  Ship Method
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "ship_to" ? sortOrder : false}
              >
                <TableSortLabel
                  active={orderBy === "ship_to"}
                  direction={orderBy === "ship_to" ? sortOrder : "asc"}
                  onClick={() => handleSort("ship_to")}
                >
                  Ship To
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "ship_from" ? sortOrder : false}
              >
                <TableSortLabel
                  active={orderBy === "ship_from"}
                  direction={orderBy === "ship_from" ? sortOrder : "asc"}
                  onClick={() => handleSort("ship_from")}
                >
                  Ship From
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "ship_date" ? sortOrder : false}
              >
                <TableSortLabel
                  active={orderBy === "ship_date"}
                  direction={orderBy === "ship_date" ? sortOrder : "asc"}
                  onClick={() => handleSort("ship_date")}
                >
                  Ship Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "created_at" ? sortOrder : false}
              >
                <TableSortLabel
                  active={orderBy === "created_at"}
                  direction={orderBy === "created_at" ? sortOrder : "asc"}
                  onClick={() => handleSort("created_at")}
                >
                  Date Created
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shipsLoading && shipments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : sortedShipments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    {searchTerm
                      ? "No Shipments found matching your search."
                      : "No Shipments found."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedShipments.map((shipment) => (
                <TableRow key={shipment.id} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {shipment.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={stringUtils.capFirstLetter(
                        shipment.status.replace(/_/g, " "),
                      )}
                      color={getStatusColor(shipment.status)}
                      size="small"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        shipment.reason === "OTHER"
                          ? `OTHER: ${shipment.reason_other}`
                          : shipment.reason
                      }
                      color={
                        shipment.reason === "OTHER" ? "warning" : "default"
                      }
                      size="small"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        shipment.carrier === "OTHER"
                          ? `OTHER: ${shipment.carrier_other}`
                          : shipment.carrier
                      }
                      color={
                        shipment.carrier === "OTHER" ? "warning" : "default"
                      }
                      size="small"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{shipment.method}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {shipment.ship_to.name}
                      <br />
                      {shipment.ship_to.address_line1}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {shipment.ship_from.name}
                      <br />
                      {shipment.ship_from.address_line1}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {shipment.shipment_date
                        ? new Date(shipment.shipment_date).toLocaleDateString()
                        : "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(shipment.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/shipments/form?mode=view&id=${shipment.id}`,
                            )
                          }
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Shipment">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/shipments/form?mode=edit&id=${shipment.id}`,
                            )
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
    </Box>
  );
};
